import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useScopeParams } from "./useScopeParams";
import { chatService } from "../services/chatService";
import { normalizeChatHistory } from "../components/Chat/utils";
import type { ChatMessage } from "../components/Chat/chat";

const getHistoryQueryKey = (scope: string, id: string) =>
  ["chat-history", scope, id] as const;

export function useAskVivoHistory() {
  const { scope, id } = useScopeParams();
  const queryClient = useQueryClient();
  const historyQueryKey = useMemo(
    () => getHistoryQueryKey(scope, id),
    [scope, id],
  );
  const latestHistorySnapshotRef = useRef<ChatMessage[]>([]);
  const deletedHistorySessionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    latestHistorySnapshotRef.current = [];
    deletedHistorySessionIdsRef.current = new Set();
  }, [scope, id]);

  const historyQuery = useQuery({
    queryKey: historyQueryKey,
    queryFn: (): Promise<ChatMessage[]> =>
      chatService
        .fetchHistory(id, scope)
        .then((data) => normalizeChatHistory(data as any)),
    enabled: false,
    staleTime: 30_000,
  });

  const refetchRef = useRef(historyQuery.refetch);
  refetchRef.current = historyQuery.refetch;

  const deleteHistoryMutation = useMutation({
    mutationFn: (sessionId: string) => chatService.deleteHistoryItem(sessionId),
    onSuccess: (_data, sessionId) => {
      deletedHistorySessionIdsRef.current.add(sessionId);
      queryClient.setQueryData<ChatMessage[]>(
        historyQueryKey,
        (currentHistory) => {
          const nextHistory =
            currentHistory?.filter((chat) => chat.session_id !== sessionId) ??
            [];
          latestHistorySnapshotRef.current = nextHistory;
          return nextHistory;
        },
      );
    },
  });

  const loadSessionMutation = useMutation({
    mutationFn: (sessionId: string) =>
      chatService
        .getHistoryDetails(sessionId)
        .then((data) => normalizeChatHistory(data as ChatMessage[])),
  });

  const fetchHistory = useCallback(async () => {
    const result = await refetchRef.current();
    const fetchedHistory = (result.data ?? []).filter(
      (chat) => !deletedHistorySessionIdsRef.current.has(chat.session_id ?? ""),
    );
    const currentHistory = latestHistorySnapshotRef.current;
    const mergedHistory = new Map<string, ChatMessage>();

    for (const chat of fetchedHistory) {
      if (chat.session_id) {
        mergedHistory.set(chat.session_id, chat);
      }
    }

    for (const chat of currentHistory) {
      const sessionId = chat.session_id;
      if (!sessionId || deletedHistorySessionIdsRef.current.has(sessionId)) {
        continue;
      }

      const existing = mergedHistory.get(sessionId);
      if (!existing) {
        mergedHistory.set(sessionId, chat);
        continue;
      }

      const existingTime = Date.parse(existing.timestamp ?? "");
      const localTime = Date.parse(chat.timestamp ?? "");
      if (
        Number.isNaN(existingTime) ||
        Number.isNaN(localTime) ||
        localTime >= existingTime
      ) {
        mergedHistory.set(sessionId, chat);
      }
    }

    const nextHistory = [...mergedHistory.values()].sort((a, b) => {
      const aTime = Date.parse(a.timestamp ?? "");
      const bTime = Date.parse(b.timestamp ?? "");
      return (
        (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime)
      );
    });

    latestHistorySnapshotRef.current = nextHistory;
    queryClient.setQueryData(historyQueryKey, nextHistory);
    return nextHistory;
  }, [historyQueryKey, queryClient]);

  const upsertHistoryItem = useCallback(
    (item: ChatMessage) => {
      if (!item.session_id) return;
      deletedHistorySessionIdsRef.current.delete(item.session_id);

      queryClient.setQueryData<ChatMessage[]>(
        historyQueryKey,
        (currentHistory = []) => {
          const nextHistory = [...currentHistory];
          const existingIndex = nextHistory.findIndex(
            (chat) => chat.session_id === item.session_id,
          );

          if (existingIndex >= 0) {
            nextHistory[existingIndex] = item;
          } else {
            nextHistory.unshift(item);
          }

          latestHistorySnapshotRef.current = nextHistory;
          return nextHistory;
        },
      );
    },
    [historyQueryKey, queryClient],
  );

  return {
    historyData: historyQuery.data ?? [],
    isHistoryLoading: historyQuery.isFetching,
    historyError: historyQuery.isError ? "Couldn't load history." : null,
    fetchHistory,
    deleteHistory: deleteHistoryMutation.mutateAsync,
    upsertHistoryItem,
    loadSession: loadSessionMutation.mutateAsync,
    isLoadingSession: loadSessionMutation.isPending,
  };
}
